import CurriculumReportingSetup from "../curriculum-reporting/CurriculumReportingSetup";
import * as React from "react";
import HomeIndex from "../home/HomeIndex";
import ConfigurationIndex from "../configuration/ConfigurationIndex";
import GradeScalesIndex from "../grading/GradeScalesIndex";
import {
    FacultiesScreen,
    ModulesScreen,
    QualificationLevelsScreen,
    QualificationsScreen,
    SchoolsScreen,
} from "../structure/StructureScreens";
import {
    AcademicProgramsScreen,
    AcademicYearsScreen,
    AssessmentCyclesScreen,
    ProgramLevelsScreen,
    QualificationModulesScreen,
    TeachingGroupsScreen,
} from "../management/ManagementScreens";
import {
    LevelEnrollmentScreen,
    ModuleEnrollmentScreen,
    TeachingGroupEnrollmentScreen,
} from "../enrollment/EnrollmentScreens";
import { SubjectChoicesScreen } from "../enrollment/SubjectChoices";
import { SubjectChoiceRulesScreen } from "../enrollment/SubjectChoiceRules";
import StructureEditor from "../activities/StructureEditor";
import MarksScreen from "../marks/MarksHome";
import ProceduresIndex from "../procedures/ProceduresIndex";
import AssignmentsIndex from "../procedures/AssignmentsIndex";
import FindingsIndex from "../procedures/FindingsIndex";
import CalculationsIndex from "../calculations/CalculationsIndex";
import AuditIndex from "../audit/AuditIndex";
import ReportsIndex from "../reporting/ReportsIndex";
import IntegrationsIndex from "../integrations/IntegrationsIndex";
import MarkImports from "../mark-imports/MarkImports";
import { ALL_ENTRIES } from "./navigation";

/**
 * The route table.
 *
 * Routes are declared here rather than inline in Shell.tsx so the shell stays about layout and this
 * file stays about wiring. Every entry in navigation.ts must have a screen in `IMPLEMENTED` - a
 * missing one is a build error rather than a silent 404, which is what keeps the sidebar and the
 * route table from drifting apart.
 *
 * Route protection is server-side. Program.cs sets a fail-closed fallback policy, so an unauthorised
 * API call fails at the API; the client-side job is to hide affordances (navigation.ts) and to
 * disable controls (usePermission), not to be the gate.
 */

export interface AppRoute {
    path: string;
    element: React.ReactNode;
}

/** One entry per route declared in navigation.ts. */
const IMPLEMENTED: Record<string, React.ReactNode> = {
    "/configuration": <ConfigurationIndex />,
    "/mark-imports": <MarkImports />,
    "/mark-imports/connect": <MarkImports />,
    "/curriculum-reporting": <CurriculumReportingSetup />,
    "/grading": <GradeScalesIndex />,

    "/structure/faculties": <FacultiesScreen />,
    "/structure/schools": <SchoolsScreen />,
    "/structure/qualifications": <QualificationsScreen />,
    "/structure/qualification-levels": <QualificationLevelsScreen />,
    "/structure/modules": <ModulesScreen />,

    "/management/academic-years": <AcademicYearsScreen />,
    "/management/cycles": <AssessmentCyclesScreen />,
    "/management/academic-programs": <AcademicProgramsScreen />,
    "/management/program-levels": <ProgramLevelsScreen />,
    "/management/qualification-modules": <QualificationModulesScreen />,
    "/management/teaching-groups": <TeachingGroupsScreen />,

    "/enrollment/subject-choices": <SubjectChoicesScreen />,
    "/enrollment/subject-choice-rules": <SubjectChoiceRulesScreen />,
    "/enrollment/modules": <ModuleEnrollmentScreen />,
    "/enrollment/levels": <LevelEnrollmentScreen />,
    "/enrollment/teaching-groups": <TeachingGroupEnrollmentScreen />,

    "/activities": <StructureEditor />,
    "/marks": <MarksScreen />,

    "/procedures": <ProceduresIndex />,
    "/assignments": <AssignmentsIndex />,
    "/findings": <FindingsIndex />,
    "/calculations": <CalculationsIndex />,
    "/audit": <AuditIndex />,
    "/reports": <ReportsIndex />,
    "/integrations": <IntegrationsIndex />,
};

export const routes: AppRoute[] = [
    { path: "/", element: <HomeIndex /> },

    ...ALL_ENTRIES.map((entry) => {
        const element = IMPLEMENTED[entry.route];
        if (!element) {
            // Loud rather than silent: a nav entry with no screen would otherwise render an empty
            // page, which is indistinguishable from one that failed to load.
            throw new Error(`navigation.ts declares "${entry.route}" but routes.tsx has no screen for it.`);
        }
        // A trailing /* lets a screen own its own nested routes (detail pages, tabs).
        return { path: `${entry.route}/*`, element };
    }),
];
